<template>
  <div>
    <div class="flex" v-if="!link && !uploadLink">
      <button
        :class="[
          isTrackLink
            ? 'text-grayscale-96 font-semibold border-grayscale-96'
            : 'text-grayscale-60 border-grayscale-16',
          'flex-1 text-center pb-4 border-b',
        ]"
        @click="isTrackLink = true"
      >
        Link
      </button>
      <button
        :class="[
          !isTrackLink
            ? 'text-grayscale-96 font-semibold border-grayscale-96'
            : 'text-grayscale-60 border-grayscale-16',
          'flex-1 text-center pb-4 border-b',
        ]"
        @click="isTrackLink = false"
      >
        MP3/WAV
      </button>
    </div>
    <div v-else class="flex justify-between items-center text-grayscale-40">
      <p
        class="text-sm max-w-3/4 overflow-ellipsis whitespace-nowrap overflow-hidden"
      >
        {{ link || file?.name }}
      </p>
      <button @click="confirmDelete = true">
        <i class="icon-trash text-20" />
      </button>
    </div>

    <ErrorMessage
      class="mt-6"
      :isVisible="uploadError.length > 0"
      :message="uploadError"
    />
    <div
      :class="[(link && !validForm) || !link ? 'mb-14' : '', 'mt-8']"
      v-if="isTrackLink"
    >
      <loading :isLoading="_isLoading">
        <div class="w-full" v-if="!link">
          <input-field
            :value="link"
            name="link"
            placeholder="https://..."
            type="url"
            bg-color="bg-grayscale-10"
            @update:value="linkChanged"
            @proceed="checkInput"
          />
          <div
            class="flex ml-auto justify-center gap-6 mt-4 text-xl leading-normal"
          >
            <i
              :class="[
                link.includes('soundcloud.com')
                  ? 'text-grayscale-96'
                  : 'text-grayscale-50',
                'icon-soundcloud',
              ]"
            />
            <i
              :class="[
                link.includes('spotify.com')
                  ? 'text-grayscale-96'
                  : 'text-grayscale-50',
                'icon-spotify',
              ]"
            />
            <i
              :class="[
                link.includes('youtube.com')
                  ? 'text-grayscale-96'
                  : 'text-grayscale-50',
                'icon-youtube',
              ]"
            />
          </div>
        </div>
        <ErrorMessage
          class="mt-6"
          :isVisible="errors.linkError !== ''"
          :message="errors.linkError"
        />
        <div v-if="errors.linkError !== ''" class="w-full text-left mt-8">
          <div class="text-13 font-normal leading-160 text-grayscale-96">
            <span class="font-bold">You may add links to:</span>
            <ul class="pl-5 list-disc mt-2">
              <li>Spotify tracks</li>
              <li>SoundCloud tracks (public or private)</li>
              <li>Youtube videos (public or unlisted)</li>
            </ul>
          </div>
          <div class="mt-6 text-13 font-normal leading-160 text-grayscale-96">
            <span class="font-bold">You cannot add:</span>
            <ul class="pl-5 list-disc mt-2">
              <li>Spotify profiles, playlists or albums</li>
              <li>Youtube private videos</li>
              <li>Any other streaming services or links</li>
            </ul>
          </div>
        </div>
      </loading>
      <div
        v-if="previewUrl && !isPrivate"
        class="bg-card-background mt-6 rounded-8"
      >
        <iframe
          class="w-full h-20 rounded-12"
          :src="previewUrl"
          frameborder="0"
          allowtransparency="true"
          allow="encrypted-media"
        ></iframe>
      </div>
      <a
        v-if="isPrivate"
        :href="previewUrl"
        target="_blank"
        class="bg-card-background mb-2 p-4 rounded-8 w-full flex items-center justify-between gap-4"
      >
        <img class="w-12 h-12" :src="thumbnailUrl" alt="thumbnail" />
        <div
          class="truncate whitespace-nowrap overflow-hidden flex flex-col items-start"
        >
          <p class="font-bold">{{ title }}</p>
          <a :href="previewUrl">{{ previewUrl }}</a>
        </div>
        <i class="icon-play" />
      </a>
    </div>
    <div
      :class="[!uploadLink ? 'mb-14' : '', 'mt-4 text-grayscale-80']"
      v-if="!isTrackLink"
    >
      <div
        v-if="!uploadLink"
        class="w-full border-dashed border rounded-4"
        :class="
          isDragActive
            ? 'border-accent-orange bg-grayscale-16'
            : 'border-grayscale-40'
        "
      >
        <div class="flex flex-col p-6 gap-4" v-if="_isLoading">
          <div class="flex w-full justify-between items-center">
            <p class="text-grayscale-96 font-bold text-base max-w-80">
              {{ file?.name }}
            </p>
            <div
              class="w-8 h-8 flex items-center justify-center bg-grayscale-16 rounded-full cursor-pointer"
              @click="removeFile"
            >
              <i class="icon-close text-2xl leading-6 text-grayscale-40" />
            </div>
          </div>
          <div class="flex flex-col items-start w-full">
            <div class="h-3px w-full bg-grayscale-30 rounded-2">
              <div
                class="h-full bg-grayscale-96 rounded-2"
                :style="`width: ${progressPercent}%`"
              ></div>
            </div>
            <p v-if="file" class="text-grayscale-60 text-sm font-normal">
              {{ progressPercent }}% of
              {{ (file.size / 1000000).toFixed(2) }} MB
            </p>
          </div>
        </div>
        <div
          for="upload-track"
          v-else
          class="flex flex-col h-60 cursor-pointer relative items-center"
        >
          <div class="mt-20 mb-6">
            <i class="text-3xl m-auto icon-plus rounded-full border" />
          </div>
          <input
            type="file"
            ref="hiddenInput"
            id="upload-track"
            class="absolute inset-0 z-50 w-full h-full p-0 m-0 outline-none opacity-0 cursor-pointer"
            accept=".mp3, .wav, .flac, .m4a, .aac, .ogg"
            @change.prevent="onFileSelected"
            tabindex="-1"
            @dragover="isDragActive = true"
            @dragleave="isDragActive = false"
            @drop="isDragActive = false"
          />
          <span class="font-bold">MP3 OR WAV</span>
          <span>Your upload will be kept secure</span>
        </div>
      </div>
      <div v-else class="w-full rounded-4">
        <div class="bg-grayscale-13 px-4 py-3 mb-1 rounded-8">
          <ClientOnly>
            <AudioPreview
              :file="uploadLink"
              v-if="uploadLink"
              :key="uploadLink"
            />
          </ClientOnly>
        </div>
        <input-field
          v-if="uploadLink"
          v-model:value="title"
          name="title"
          placeholder="Title"
          label="Title"
          :is-mandatory="true"
          class="text-start"
          type="text"
          bg-color="bg-grayscale-10"
          v-model:error-message="errors.titleError"
          @clearError="clearError"
          @proceed="checkInput"
        />
      </div>
    </div>

    <div v-if="(link || uploadLink) && !_isLoading">
      <div class="border-y border-grayscale-16 mt-16 text-sm font-bold">
        <div
          v-if="!hideAddToXposureMusicCatalog"
          class="flex justify-between py-4 border-b border-grayscale-16"
        >
          <p>Add to Xposure Music Catalog</p>
          <SwitchXP v-model:value="addToCatalog" />
        </div>
        <div v-if="!link.includes('spotify')" class="flex justify-between py-4">
          <p>Track is released</p>
          <SwitchXP v-model:value="isReleased" />
        </div>
      </div>

      <div class="text-left mt-16">
        <p class="font-medium text-sm text-grayscale-60">
          What did you do on this track?
          <span class="text-accent-magenta">*</span>
        </p>
        <p class="text-xs text-grayscale-40 mb-2">
          You’ll be able to ask the Pro questions later in the flow.
        </p>

        <input-field
          v-model:value="description"
          name="description"
          type="textarea"
          placeholder="Produce, write, perform, promote..."
          :is-disabled="_isLoading"
          label-class="font-14 text-manatee"
          full-container-class="w-full"
          bg-color="bg-grayscale-10"
          :max-length="200"
          :hideMaxLength="!link"
          v-model:error-message="errors.descriptionError"
          @clearError="clearError"
        />
      </div>
    </div>

    <div
      v-if="errors.linkError === '' && (validForm || uploadLink)"
      class="mt-8 flex justify-center"
      :class="containerClass"
    ></div>

    <div class="absolute w-full h-full top-0 left-0" v-if="confirmDelete">
      <ConfirmationModal
        title="Are you sure you want to delete this track?"
        description="We will remove this track from your list of tracks, however, it will remain attached to all requests you have made up to this point."
        confirm-title="Delete"
        @close="confirmDelete = false"
        @confirm="resetForm"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import InputField from "@/components/layout/inputs/InputField.vue";
import Loading from "@/components/layout/Loading.vue";
import ErrorMessage from "@/components/layout/ErrorMessage.vue";
import { useLocalNotificationsStore } from "@/store/notifications";
import { useTrackStore } from "@/store/track";
import ApiError from "@/types/error";
import { TrackError } from "@/types/track";
import { debounce, scrollToFirstError } from "@/utils/helpers";
import { validateField, validateLink } from "@/utils/validation";
import { computed, onMounted, reactive, ref } from "vue";
import { UploadItem, UploadScope } from "@/types/upload";
import { User } from "@/types/user";
import { useUserStore } from "@/store/user";
import UploadService from "@/services/upload-service";
import AudioPreview from "../feedback/AudioPreview.vue";
import axios, { CancelTokenSource } from "axios";
import { useProductsStore } from "@/store/products";
import { ProductType } from "@/types/product";
import { useAuthenticationStore } from "@/store/authentication";
import SwitchXP from "@/components/layout/inputs/Switch.vue";
import ConfirmationModal from "@/components/feedback/pro/components/ConfirmationModal.vue";

const router = useRouter();

const userAgent = navigator.userAgent || navigator.vendor;
const containerClass = computed(() => {
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    const chromeAgent =
      userAgent.indexOf("Chrome") > -1 || /Google Inc/.test(navigator.vendor);
    let safariAgent =
      userAgent.indexOf("Safari") > -1 &&
      /Apple Computer/.test(navigator.vendor);
    if (chromeAgent && safariAgent) safariAgent = false;
    return safariAgent ? "mb-24" : "";
  } else {
    return "";
  }
});

const emit = defineEmits(["close", "add", "linkUpdated", "update:isLoading"]);

const authStore = useAuthenticationStore();
const trackStore = useTrackStore();
const userStore = useUserStore();
const localNotifications = useLocalNotificationsStore();
const uploadService = new UploadService("uploads");

const confirmDelete = ref(false);
const isTrackLink = ref(true);
const link = ref("");
const uploadLink = ref("");
const title = ref("");
const isDragActive = ref(false);
const preview = computed(() => trackStore.preview);
const previewUrl = ref("");
const isPrivate = ref(false);
const thumbnailUrl = ref("false");
const fileExtension = ref("");

const _isLoading = computed({
  get: () => props.isLoading,
  set: (value) => emit("update:isLoading", value),
});

const progressPercent = ref();
const isReleased = ref(true);
const addToCatalog = ref(true);
let axiosSource: CancelTokenSource | undefined;
const errors = reactive({
  linkError: "",
  titleError: "",
  descriptionError: "",
} as TrackError);
const uploadError = ref("");
const hiddenInput = ref<HTMLInputElement>();
const file = ref<File | null>(null);
const user = ref<User>(userStore.userData);
const validForm = computed(() => {
  if (
    (isTrackLink.value && !link.value) ||
    (!isTrackLink.value && !uploadLink.value)
  )
    return false;
  if (errors.linkError || errors.titleError || errors.descriptionError)
    return false;
  if (!title.value) return false;
  return true;
});
const description = ref();
const productStore = useProductsStore();

onMounted(async () => {
  await trackStore.getTracks("");
});

const closeModal = () => {
  resetForm();
  emit("close");
};

const props = defineProps({
  hideAddToXposureMusicCatalog: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
});

const resetForm = () => {
  // TODO remove the file from S3

  confirmDelete.value = false;
  link.value = "";
  uploadLink.value = "";
  previewUrl.value = "";
  title.value = "";
  description.value = "";
  errors.linkError = "";
  errors.titleError = "";
  errors.descriptionError = "";
  emit("linkUpdated", "");
};

const checkInput = async (): Promise<boolean> => {
  description.value = description.value?.trim();
  title.value = title.value.trim();

  _isLoading.value = true;
  if (isTrackLink.value) errors.linkError = validateLink(link.value);
  errors.titleError = validateField(title.value, "title", "input");

  if (!title.value) {
    _isLoading.value = false;
    errors.titleError = "Please enter a title";
    return false;
  }

  if (!description.value) {
    _isLoading.value = false;
    errors.descriptionError = "Please enter a description";
    return false;
  }

  if (errors.linkError || errors.titleError || errors.descriptionError) {
    scrollToFirstError(errors);
    _isLoading.value = false;
    if (
      [ProductType.longCall, ProductType.shortCall].includes(
        productStore.feedbackRequest.type!,
      )
    ) {
      emit("add");
      closeModal();
      return true;
    }
    return false;
  }
  try {
    _isLoading.value = true;
    const track = await trackStore.addTrack(
      title.value,
      isTrackLink.value ? link.value : uploadLink.value,
      description.value,
      isPrivate.value,
      router.currentRoute.value.path,
      isReleased.value,
      props.hideAddToXposureMusicCatalog ? false : addToCatalog.value,
      fileExtension.value,
    );

    emit("add", track);
    closeModal();
    resetForm();
  } catch (error) {
    const errorMessage = (error as ApiError).message;
    localNotifications.addNotification({
      message: errorMessage,
      isError: true,
    });
  } finally {
    _isLoading.value = false;
  }

  _isLoading.value = false;
  return true;
};

defineExpose({
  closeModal,
  checkInput,
});

const clearError = (inputName: keyof TrackError): void => {
  if (Object.prototype.hasOwnProperty.call(errors, `${inputName}Error`)) {
    (errors as any)[`${inputName}Error`] = "";
  }
};

const setLink = async (value: string) => {
  _isLoading.value = true;
  errors.linkError = validateLink(value);
  if (errors.linkError) {
    emit("linkUpdated", "");
    previewUrl.value = "";
    isPrivate.value = false;
    thumbnailUrl.value = "";
    _isLoading.value = false;
    return;
  }
  if (value != "") {
    emit("linkUpdated", value);
    link.value = value;
    try {
      await trackStore.getPreview(link.value);

      title.value = preview.value.title || "";
      previewUrl.value = preview.value.previewUrl || "";
      isPrivate.value = preview.value.isPrivate || false;
      thumbnailUrl.value = preview.value.thumbnailUrl || "";
    } catch (error: any) {
      previewUrl.value = "";
      errors.linkError = error.message;
    }
  } else {
    emit("linkUpdated", "");
    previewUrl.value = "";
    isPrivate.value = false;
    thumbnailUrl.value = "";
  }
  _isLoading.value = false;
};

const removeFile = () => {
  file.value = null;
  uploadLink.value = "";
  uploadError.value = "";
  _isLoading.value = false;
  axiosSource?.cancel();
};

const onFileSelected = async (event: any): Promise<void> => {
  uploadError.value = "";
  if (event.target.files.length == 0) {
    return;
  }

  file.value = event.target.files[0];

  if (!file.value) {
    alert(JSON.stringify(file.value));
    return;
  }

  if (file.value.size > 50000000) {
    uploadError.value = `The file is ${(file.value.size / 1000000).toFixed(
      2,
    )} MB exceeding the maximum file size of 50 MB`;
    return;
  }

  _isLoading.value = true;
  try {
    const data: UploadItem = {
      name: "track",
      scope: UploadScope.userTrack,
      owner: user.value._id,
      file: file.value,
    };
    progressPercent.value = 0;
    axiosSource = axios.CancelToken.source();
    const uploadResponse = await uploadService.uploadFileWithAxios(
      data,
      (progress: any) => {
        progressPercent.value = (
          (progress.loaded / progress.total) *
          100
        ).toFixed(0);
      },
      axiosSource.token,
    );
    fileExtension.value = uploadResponse.upload.mimetype;
    uploadLink.value = uploadResponse.url;
    title.value = file.value.name.replace(/\.[^/.]+$/, "");
    emit("linkUpdated", uploadLink.value);
  } catch (error) {
    const errorMessage = (error as ApiError).message;
    if (errorMessage === "canceled") {
      uploadError.value = "";
      return;
    }
    localNotifications.addNotification({
      message: errorMessage,
      isError: true,
    });
  } finally {
    _isLoading.value = false;
  }

  _isLoading.value = false;
};

const linkChanged = debounce(setLink, 100);
</script>
